// Affiliate link generators with per-country store selection.
// Tags come from REACT_APP_*_TAG env vars; replace placeholders in /app/frontend/.env
// with your real affiliate IDs after registering with each program.

const TAGS = {
    amazon: process.env.REACT_APP_AMAZON_TAG || "tuafiliado-21",
    casadellibro: process.env.REACT_APP_CASA_DEL_LIBRO_TAG || "",
    fnac: process.env.REACT_APP_FNAC_TAG || "",
    corteingles: process.env.REACT_APP_CORTE_INGLES_TAG || "",
};

// Awin (Affiliate Window) deep-link config — used for Casa del Libro.
// Awin uses a single redirect URL per click:
//   https://www.awin1.com/cread.php?awinmid={MID}&awinaffid={PUB}&ued={DEST}
// MID is the merchant's program ID (Casa del Libro = 21491).
// PUB is our Publisher ID from Awin → Profile → Account.
const AWIN = {
    publisherId: process.env.REACT_APP_AWIN_PUBLISHER_ID || "",
    casaDelLibroMid: process.env.REACT_APP_AWIN_CASA_DEL_LIBRO_MID || "",
};

const buildAwinDeepLink = (mid, destUrl) => {
    if (!AWIN.publisherId || !mid) return destUrl;
    return (
        `https://www.awin1.com/cread.php?awinmid=${encodeURIComponent(mid)}` +
        `&awinaffid=${encodeURIComponent(AWIN.publisherId)}` +
        `&ued=${encodeURIComponent(destUrl)}`
    );
};

// Append `&{key}={value}` ONLY when the tag is configured AND not the obvious
// placeholder. Empty/placeholder tags trigger anti-bot blocks on some stores.
const tagParam = (key, value) =>
    value && value !== "tuafiliado" ? `&${key}=${encodeURIComponent(value)}` : "";

const encode = (s) => encodeURIComponent(s || "");

// Amazon per-country domains
const AMAZON_DOMAIN = {
    ES: "amazon.es",
    MX: "amazon.com.mx",
    US: "amazon.com",
    AR: "amazon.com",
    CL: "amazon.com",
    CO: "amazon.com",
    PE: "amazon.com",
    UY: "amazon.com",
    VE: "amazon.com",
    UK: "amazon.co.uk",
    GB: "amazon.co.uk",
    FR: "amazon.fr",
    DE: "amazon.de",
    IT: "amazon.it",
    PT: "amazon.es",
    BR: "amazon.com.br",
    CA: "amazon.ca",
    JP: "amazon.co.jp",
    AU: "amazon.com.au",
};

// FNAC operates in Spain, France, Portugal, Belgium, Switzerland.
const FNAC_DOMAIN = {
    ES: "fnac.es",
    FR: "fnac.com",
    PT: "fnac.pt",
    BE: "fr.fnac.be",
    CH: "fr.fnac.ch",
};

// El Corte Inglés operates in Spain and Portugal.
const CORTE_INGLES_DOMAIN = {
    ES: "elcorteingles.es",
    PT: "elcorteingles.pt",
};

// Country → which stores to show. Spanish-speaking audience gets FNAC + El
// Corte Inglés alongside Amazon. Other markets keep Amazon-only since the
// alternative chains aren't present (or FNAC where it operates in EU).
//
// `cegal` (todostuslibros.com) sits at the END of the Spanish list intentionally
// — it's our "indie bookshop" CTA and is rendered with a distinct visual
// treatment by BookCard so it doesn't compete visually with the chains.
const STORES_BY_COUNTRY = {
    ES: ["fnac", "amazon", "casadellibro", "corteingles", "cegal"],
    PT: ["fnac", "amazon", "corteingles"],
    FR: ["fnac", "amazon"],
    BE: ["fnac", "amazon"],
    CH: ["fnac", "amazon"],
    MX: ["amazon"],
    AR: ["amazon"],
    CL: ["amazon"],
    CO: ["amazon"],
    PE: ["amazon"],
    UY: ["amazon"],
    VE: ["amazon"],
    US: ["amazon"],
    UK: ["amazon"],
    GB: ["amazon"],
    DE: ["amazon"],
    IT: ["amazon"],
    BR: ["amazon"],
    CA: ["amazon"],
    JP: ["amazon"],
    AU: ["amazon"],
};

const DEFAULT_COUNTRY = "ES";

export function getStoresForCountry(country) {
    const c = (country || DEFAULT_COUNTRY).toUpperCase();
    const ids = STORES_BY_COUNTRY[c] || STORES_BY_COUNTRY[DEFAULT_COUNTRY];
    return ids.map((id) => STORE_BUILDERS[id]?.(c)).filter(Boolean);
}

// Spain-only retailers — these only carry books distributed in the Spanish
// market. If we can't confirm the book has a Spanish edition, we hide them
// to avoid sending users to "0 results" pages.
const SPAIN_ONLY_STORES = new Set(["fnac", "casadellibro", "corteingles", "cegal"]);

const SPANISH_LANG_TOKENS = ["spanish", "español", "espanol", "castellano", "castilian"];

const looksSpanishTitle = (title = "") => {
    // Spanish-distinctive characters or articles. Cheap heuristic — just
    // enough to spot titles that DEFINITELY were translated to Spanish.
    if (/[áéíóúñü¡¿]/i.test(title)) return true;
    if (/\b(el|la|los|las|del|de la|una|nuestro|nuestra)\b/i.test(title)) return true;
    return false;
};

/**
 * Filter the country's affiliate stores down to those likely to carry the
 * given book. Used to hide ECI / FNAC / Casa del Libro buttons for English-
 * only books (sends users straight to no-results pages otherwise).
 *
 * Rules (positive signals — any one keeps Spain stores enabled):
 *   1. ISBN starts with 978-84 → Spanish ISBN agency, edition exists in ES.
 *   2. original_language indicates Spanish.
 *   3. Title contains Spanish-distinctive characters or articles.
 * If none match, only the universal stores (Amazon) are shown.
 */
export function getAvailableStoresForBook(book, country) {
    const all = getStoresForCountry(country);
    const c = (country || DEFAULT_COUNTRY).toUpperCase();
    if (c !== "ES" && c !== "PT") return all;

    const isbnDigits = (book.isbn || "").replace(/[-\s]/g, "");
    const hasSpanishIsbn = /^97884/.test(isbnDigits);
    const langLower = (book.original_language || "").toLowerCase();
    const hasSpanishLang = SPANISH_LANG_TOKENS.some((t) => langLower.includes(t));
    const hasSpanishTitle = looksSpanishTitle(book.title || "");

    if (hasSpanishIsbn || hasSpanishLang || hasSpanishTitle) return all;
    return all.filter((s) => !SPAIN_ONLY_STORES.has(s.id));
}

const STORE_BUILDERS = {
    amazon: (country) => {
        const domain = AMAZON_DOMAIN[country] || AMAZON_DOMAIN[DEFAULT_COUNTRY];
        return {
            id: "amazon",
            name: "Amazon",
            icon: "ShoppingBag",
            // Strategy:
            // - If we have an ISBN-10 (clean, 10 chars), use the direct /dp/{ISBN}
            //   link since for printed books the ISBN-10 IS the ASIN. This goes
            //   straight to the product page in any Amazon marketplace.
            // - For ISBN-13 (or unknown), fall back to a books-category search.
            //   ISBN-13 is NOT the ASIN, so /dp/9788... typically 404s.
            build: (query, isbn) => {
                const cleanIsbn = (isbn || "").replace(/[-\s]/g, "");
                if (/^\d{9}[\dX]$/i.test(cleanIsbn)) {
                    return `https://www.${domain}/dp/${cleanIsbn}/?tag=${TAGS.amazon}`;
                }
                return `https://www.${domain}/s?k=${encode(query)}&i=stripbooks&tag=${TAGS.amazon}`;
            },
        };
    },
    casadellibro: () => ({
        id: "casadellibro",
        name: "Casa del Libro",
        icon: "BookOpen",
        // Casa del Libro is on Awin. Wrap the destination search URL in
        // the Awin redirect so clicks are tracked + commissioned.
        // Falls back to direct URL if Awin env vars aren't configured.
        build: (query) => {
            const dest = `https://www.casadellibro.com/buscar-libros?query=${encode(query)}`;
            return buildAwinDeepLink(AWIN.casaDelLibroMid, dest);
        },
    }),
    fnac: (country) => {
        const domain = FNAC_DOMAIN[country] || FNAC_DOMAIN.ES;
        return {
            id: "fnac",
            name: "FNAC",
            icon: "Library",
            // FNAC's universal book-search URL pattern. SCat=2 = books category.
            // Affiliate tag (Awin publisher ID) appended via &Origin=ipa-{ID}
            // when configured.
            build: (query) =>
                `https://www.${domain}/SearchResult/ResultList.aspx?Search=${encode(query)}&SCat=2!1${tagParam("Origin", TAGS.fnac ? `ipa-${TAGS.fnac}` : "")}`,
        };
    },
    corteingles: (country) => {
        const domain = CORTE_INGLES_DOMAIN[country] || CORTE_INGLES_DOMAIN.ES;
        return {
            id: "corteingles",
            name: "El Corte Inglés",
            icon: "ShoppingBag",
            // El Corte Inglés universal search; `cultura` filter narrows to the
            // books / culture department.
            build: (query) =>
                `https://www.${domain}/search/?s=${encode(query)}&sf=cultura${tagParam("aff", TAGS.corteingles)}`,
        };
    },
    // CEGAL (todostuslibros.com) is the official directory of independent Spanish
    // bookshops. We don't earn a commission here — this is a goodwill / mission-
    // aligned link that supports local bookshops and differentiates us from the
    // Amazon-only competition. The site auto-redirects `?keyword={ISBN}` straight
    // to the book's detail page, where the user (with their postal code stored
    // locally on todostuslibros) sees real-time availability at THEIR nearest
    // CEGAL bookshop with reservation/pickup options.
    cegal: () => ({
        id: "cegal",
        name: "Librería local",
        icon: "MapPin",
        indie: true,
        // todostuslibros.com is the official CEGAL search portal. Their
        // search results are tokenised — `?keyword=ISBN` alone gives noisy
        // matches when an ISBN isn't in their catalog (we observed this
        // in practice: a stale OpenLibrary ISBN returned 3 unrelated books).
        // Passing `title author` matches what a human types and routes
        // reliably to the correct book page (or, at worst, other works by
        // the same author in CEGAL bookshops — still useful).
        build: (query) =>
            `https://www.todostuslibros.com/busquedas?keyword=${encode(query)}`,
    }),
};
